import React, { useEffect, forwardRef, useRef, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DifferenceTypeNames, DocumentTypes, PDFTRON_DEFAULT_TOOL } from 'types';
import { inspection, getIsSingleFile, app, getGroupedDifferences } from 'store';
import { PDFManagerFactory } from 'pdftron';
import { Difference } from 'store/request/differences/types';
import DifferenceUtils from 'pdftron/docManager/Difference';
import toastEvents from 'components/DocumentToast/toastEvents';
import selectTool from 'utils/selectTool/selectTool';
import DifferenceElementView from './DifferenceElementView';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import { useDifferenceListStore } from 'zstore/differenceListStore';

interface DifferenceElementProps extends React.ComponentProps<'div'> {
  difference: Difference;
  nextDiffId: string;
  numberOfDifference: number;
  groupIndex?: number;
  listIndex: number;
  isSelected?: boolean;
  onClickSelect: (index: number) => void;
  updateCommentOpen: (id: string, open: boolean, comment?: string) => void;
  isCommentOpen: boolean;
  isKeyboardFocused: boolean;
  handleOnStopKeyboardNavigation: (index: number, behavior?: ScrollBehavior) => void;
  multipleSelectionMode?: boolean;
  isCheckboxSelected?: boolean;
  onCheckboxChange?: (difference: Difference, checked: boolean) => void;
  customStatuses?: any;
  canSuggestToCustomDictionary?: boolean;
  refetchStatuses?: () => void;
}

const DifferenceElement = memo(
  forwardRef<HTMLDivElement, DifferenceElementProps>(
    (
      {
        difference,
        nextDiffId,
        numberOfDifference,
        groupIndex,
        listIndex,
        isSelected,
        onClickSelect,
        updateCommentOpen,
        isCommentOpen,
        isKeyboardFocused,
        handleOnStopKeyboardNavigation,
        multipleSelectionMode,
        isCheckboxSelected,
        onCheckboxChange,
        customStatuses,
        canSuggestToCustomDictionary,
        refetchStatuses,
      },
      cardRef,
    ) => {
      const internalCardRef = useRef<HTMLDivElement | null>(null);
      const setDocumentToast = useDocumentToastStateStore((s) => s.setDocumentToast);
      const expireAllToast = useDocumentToastStateStore((s) => s.expireAllToast);
      const dispatch = useDispatch();
      // Group members as the numbering computed them — the same grouping the report renders as
      // N.1-N.k. Reading them from the store keeps the chip and range label in step with the card
      // number instead of the separately-fetched groups query, which can lag behind (a re-inspect
      // creates groups server-side that the query hasn't loaded yet).
      const numberedGroupMembers = useDifferenceListStore((s) => s.groupMembersByDifferenceId[difference.id]);
      // The All tab doesn't populate the store; it still uses the legacy grouping in Redux.
      const reduxGroup = useSelector(getGroupedDifferences(difference.groupId));
      const group = numberedGroupMembers ?? reduxGroup;
      const isSingleFile = useSelector(getIsSingleFile);

      useEffect(() => {
        // For insertions, re-select on source document; for everything else, re-select on target
        const docType = difference.type === DifferenceTypeNames.Insertion ? DocumentTypes.source : DocumentTypes.target;
        const annotManager = PDFManagerFactory.getViewer(docType)?.annotationManager;
        const annotations = annotManager?.getSelectedAnnotations();
        if (annotations?.length) {
          annotManager?.selectAnnotation(annotations[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [difference.id]);

      const resetDiffSelection = () => {
        // Force expire graphic annotation toast.
        expireAllToast();
        // Remove border highlight from target to match toast on difference change
        PDFManagerFactory.getViewer(DocumentTypes.target)?.removeAnnotationDocumentHighlight();
        // Reset tool and deactivate any active tools (including merge graphics tool)
        selectTool(PDFTRON_DEFAULT_TOOL);
      };

      const selectAnnotationById = (annotationId: string) => {
        const sourceAnnotationManager = PDFManagerFactory.getViewer(DocumentTypes.source)?.annotationManager;
        const targetAnnotationManager = PDFManagerFactory.getViewer(DocumentTypes.target)?.annotationManager;

        const sourceAnnot = sourceAnnotationManager?.getAnnotationById(annotationId);
        const targetAnnot = targetAnnotationManager?.getAnnotationById(annotationId);

        if (!sourceAnnot && !targetAnnot) return;
        onClickSelect(listIndex);

        if (isSingleFile && targetAnnot) {
          targetAnnotationManager?.selectAnnotation(targetAnnot);
        } else if (!isSingleFile) {
          if (sourceAnnot) {
            sourceAnnotationManager?.selectAnnotation(sourceAnnot);
          }
          // possible for markups to have only target, so also select target annot
          if (targetAnnot?.ToolName === 'MarkupTool') {
            targetAnnotationManager?.selectAnnotation(targetAnnot);
          }
        }
      };

      // select difference after saving a comment
      const handleOnSaveComment = (comment = '') => {
        resetDiffSelection();
        if (isSelected && updateCommentOpen) {
          updateCommentOpen(difference.id, false, comment);
        }
      };

      const onSelectDifference = () => {
        resetDiffSelection();
        const sourceAnnotationManager = PDFManagerFactory.getViewer(DocumentTypes.source)?.annotationManager;
        const targetAnnotationManager = PDFManagerFactory.getViewer(DocumentTypes.target)?.annotationManager;
        sourceAnnotationManager?.deselectAllAnnotations();
        targetAnnotationManager?.deselectAllAnnotations();
        const isNoMatchEmbeddedImage =
          difference.type === DifferenceTypeNames.EmbeddedImage &&
          difference.embeddedImageDetails?.matchDetails?.[0].confidenceLevel === 'None' &&
          !difference.embeddedImageDetails?.userLinkedMatch;
        const isUserLinkedEmbeddedImage =
          difference.type === DifferenceTypeNames.EmbeddedImage && !!difference.embeddedImageDetails?.userLinkedMatch;

        // If this was triggered by auto-advance, always select (avoid toggling off)
        const isAutoAdvance = internalCardRef.current?.dataset?.autoAdvance === 'true';

        if (!isSelected || isAutoAdvance) {
          if (difference.type === DifferenceTypeNames.Deletion) {
            setDocumentToast(DocumentTypes.target, toastEvents.deletionDifference);
          } else if (difference.type === DifferenceTypeNames.Insertion) {
            setDocumentToast(DocumentTypes.source, toastEvents.insertionDifference);
          } else if (isNoMatchEmbeddedImage) {
            // Determine which document has the deletion marker
            const embeddedImageDeletionTarget = DifferenceUtils.getEmbeddedImageDeletionTarget(difference);
            const toastDocumentType =
              embeddedImageDeletionTarget === 'source' ? DocumentTypes.source : DocumentTypes.target;
            setDocumentToast(toastDocumentType, toastEvents.noMatchEmbeddedImage);
          } else if (difference.type === DifferenceTypeNames.UnmatchedAnnotation) {
            setDocumentToast(DocumentTypes.target, toastEvents.annotationDifference);
          } else if (difference.type === DifferenceTypeNames.FontMetrics) {
            const fontSizeStr = difference.fontMetricsDetails?.fontSizeCheckValue?.toString() || '';

            if (difference.fontMetricsDetails?.isGlobalFontResult) {
              const contextTag = difference.fontMetricsDetails?.contextTag;
              const toastType = contextTag === 'Pass' ? toastEvents.fontMetricsPass : toastEvents.fontMetricsFail;

              const customToast = {
                ...toastType,
                message: toastType.message.replace('{fontPointSize}', fontSizeStr),
              };

              setDocumentToast(DocumentTypes.target, customToast);
              // Add border highlight for global font metrics result
              if (contextTag === 'Pass') {
                PDFManagerFactory.getViewer(DocumentTypes.target)?.addGreenDocumentHighlight();
              } else {
                PDFManagerFactory.getViewer(DocumentTypes.target)?.addRedDocumentHighlight();
              }
            } else {
              // Not an overall result, show partial fail
              const customToast = {
                ...toastEvents.fontMetricsPartialFail,
                message: toastEvents.fontMetricsPartialFail.message.replace('{fontPointSize}', fontSizeStr),
              };

              setDocumentToast(DocumentTypes.target, customToast);
            }
          }

          // For grouped differences, always select the first difference in the group
          const idToSelect = group && group.length > 0 ? group[0].id : difference.id;

          // TODO: TEMPORARY MOCK - Color differences may not have annotations yet;
          // directly focus them without requiring a PDF annotation.
          // Remove this block when real color inspection service is integrated.
          if (difference.type === DifferenceTypeNames.Color || isNoMatchEmbeddedImage || isUserLinkedEmbeddedImage) {
            onClickSelect(listIndex);
            dispatch(inspection.actions.focusDifference(idToSelect));
          } else {
            selectAnnotationById(idToSelect);
          }
        } else {
          dispatch(inspection.actions.unfocusDifference());
          dispatch(app.actions.setFocusedMatchIndex(-1));
        }
      };

      const toggleCommentOpen = (
        e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | KeyboardEvent,
        open: boolean,
      ) => {
        if (isSelected) {
          e.stopPropagation();
        }
        updateCommentOpen(difference.id, open);
      };

      // After we refactor all differences to use the new DifferenceCardLayout approach, move the rendering logic from DifferenceElementView here.
      return (
        <DifferenceElementView
          elementRef={(el: HTMLDivElement | null) => {
            internalCardRef.current = el;
            if (typeof cardRef === 'function') {
              (cardRef as (instance: HTMLDivElement | null) => void)(el);
            } else if (cardRef && typeof cardRef === 'object') {
              (cardRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
            }
          }}
          numberOfDifference={numberOfDifference}
          groupIndex={groupIndex}
          selectDifference={onSelectDifference}
          isSelected={!!isSelected}
          handleToggleComment={toggleCommentOpen}
          isCommentOpen={isCommentOpen}
          difference={difference}
          groupLength={group ? group.length : 0}
          isKeyboardFocused={isKeyboardFocused}
          nextDiffId={nextDiffId}
          handleOnSaveComment={handleOnSaveComment}
          handleOnStopKeyboardNavigation={handleOnStopKeyboardNavigation}
          multipleSelectionMode={multipleSelectionMode}
          isCheckboxSelected={isCheckboxSelected}
          onCheckboxChange={onCheckboxChange}
          customStatuses={customStatuses}
          canSuggestToCustomDictionary={canSuggestToCustomDictionary}
          refetchStatuses={refetchStatuses}
        />
      );
    },
  ),
);

export default DifferenceElement;
